import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import App from './App'
import { UiLanguageProvider } from './i18n'
import './styles.css'

// Stripe imports
import { Elements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'

function ErrorBoundary({ children }: { children: React.ReactNode }) {
  const [err, setErr] = React.useState<Error | null>(null)

  React.useEffect(() => {
    const onError = (e: ErrorEvent) => setErr(e.error || new Error(e.message))
    const onRejection = (e: PromiseRejectionEvent) =>
      setErr(e.reason instanceof Error ? e.reason : new Error(String(e.reason)))
    window.addEventListener('error', onError)
    window.addEventListener('unhandledrejection', onRejection)
    return () => {
      window.removeEventListener('error', onError)
      window.removeEventListener('unhandledrejection', onRejection)
    }
  }, [])

  if (err) {
    return (
      <div className="container">
        <div className="card">
          <h3>Something went wrong</h3>
          <pre style={{ whiteSpace: 'pre-wrap' }}>{String(err.message || err)}</pre>
        </div>
      </div>
    )
  }

  return <>{children}</>
}

// Load Stripe publishable key once — and cache across HMR
const pk = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string | undefined
if (!pk) {
  console.error('[Stripe] Missing VITE_STRIPE_PUBLISHABLE_KEY')
}

// Reuse a single promise across the app & hot reloads
declare global {
  interface Window {
    __stripePromise?: Promise<import('@stripe/stripe-js').Stripe | null>
  }
}

if (!window.__stripePromise) {
  window.__stripePromise = pk ? loadStripe(pk) : Promise.resolve(null)
}

const stripePromise = window.__stripePromise

const base = (import.meta.env.BASE_URL || '/').replace(/\/+$/, '') || '/'

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    {stripePromise ? (
      <Elements stripe={stripePromise}>
        <BrowserRouter basename={base}>
          <UiLanguageProvider>
            <ErrorBoundary>
              <App />
            </ErrorBoundary>
          </UiLanguageProvider>
        </BrowserRouter>
      </Elements>
    ) : (
      <BrowserRouter basename={base}>
        <UiLanguageProvider>
          <ErrorBoundary>
            <div className="container">
              <div className="banner error" style={{ marginTop: 12 }}>
                Stripe isn’t configured — set <code>VITE_STRIPE_PUBLISHABLE_KEY</code> in your
                frontend <code>.env</code>.
              </div>
              <App />
            </div>
          </ErrorBoundary>
        </UiLanguageProvider>
      </BrowserRouter>
    )}
  </React.StrictMode>
)
